/* The onboarding tour script.
 *
 * Written for someone who has never opened this app: what the banks and the queues are, how a
 * headline is built, and which switch hands work over to Adorian. Kept in one file, separate from
 * the machinery that renders it, so the copy can be edited without touching any behaviour.
 *
 * Each step points at a CSS selector. If that element is not on the page — an empty series,
 * a panel that is closed, a list with nothing in it — the step still shows, centred, with the
 * same words. A tour that dead-ends on a fresh account teaches nobody anything.
 */

/* The chapters. A deep link runs ONE of these and stops at its end — landing on the readiness
   banner and then being walked through Intake and Adorian's front door is not what a "?" next to
   that banner promised. The full tour from the top bar runs them all, in this order. */
/* ⚠️ 'endless' is the REAL bank and keeps its name, because the chapter name is a deep link:
   `?tour=endless` has been pasted into Slack and still has to land somewhere sensible. 'fake' is
   the bank that split out of it on 2026-08-21. */
export const CHAPTERS = ['basics', 'building', 'shoot-day', 'endless', 'fake', 'intake', 'handover'] as const;
export type Chapter = (typeof CHAPTERS)[number];

export interface TourStep {
  /** Which chapter this belongs to. A deep link runs from a step to the end of its chapter. */
  chapter: Chapter;
  /* A stable slug, on the steps something links to. `?tour=<id>` starts here, and so does the
     "?" rendered next to the feature. Changing one is changing a URL people may have pasted. */
  id?: string;
  /** Route to be on for this step. Omitted = stay where we are. */
  path?: string | ((seriesId: string) => string);
  /** What to spotlight. Omitted = a centred card with no spotlight. */
  target?: string;
  title: string;
  body: string;
  /* The target may legitimately not be there. A step about problems cannot require problems to
     exist — finish the outstanding artwork and the readiness banner disappears, which is a GOOD
     state that would otherwise fail the tour's own "every target was found" test. These fall
     back to a centred card, so their copy has to read correctly with nothing highlighted. */
  optional?: true;
  /** Opens a panel or ticks a box so the step has something to point at. NEVER writes data. */
  setUp?: 'open-csv' | 'open-history' | 'open-trash' | 'open-plays'
    | 'close-panels' | 'pick-first' | 'unpick-all' | 'clear-filters'
    | 'open-explainer' | 'restore-explainer';
}

export const TOUR_STEPS: TourStep[] = [
  // ── the shape of the thing ────────────────────────────────────────────────────────────────
  {
    chapter: 'basics',
    path: '/',
    title: 'Welcome to Real or Fake',
    body: 'Adorian reads a headline on camera, guesses whether it is real or fabricated, then hits REVEAL. You build what he reads. The whole tour is about five minutes — or click the small ? next to anything to jump straight to that part.',
  },
  {
    chapter: 'basics',
    path: '/',
    target: '.app-nav',
    title: 'Four places headlines live',
    body: 'Series are planned episodes, played in order. Real Headlines and Fake Headlines are two open banks with no episode around them — split apart because a found headline and a written one are different jobs. Intake is a waiting room for headlines collected automatically that nobody has read yet. A headline sits in exactly one of the four, so nothing in a series can turn up in a bank, or the other way round.',
  },

  // ── the series list ───────────────────────────────────────────────────────────────────────
  {
    chapter: 'basics',
    path: '/',
    target: '.inline-form',
    title: 'Start a series',
    body: 'Type a name and hit Create Series. Leave the box empty and it takes the next number for you. Names are internal — Adorian never sees them, so call them whatever helps you.',
  },
  {
    chapter: 'basics',
    path: '/',
    target: '.round-card',
    title: 'One card per series',
    body: 'Click anywhere on a card to open it. The line underneath tells you how many headlines it holds and whether any of them would break a recording.',
  },
  {
    chapter: 'basics',
    path: '/',
    target: '.round-title-btn',
    title: 'Rename without opening it',
    body: 'Click the name to edit it right here. Enter saves, Escape throws the edit away.',
  },
  {
    chapter: 'basics',
    path: '/',
    target: '.round-status-select',
    title: 'This is the hand-over switch',
    body: 'Status decides what Adorian can pick up. Only a series set to READY TO RECORD appears on his screen — everything else stays invisible to him. Set it to Recorded once it is shot, and Archived to put it away.',
  },
  {
    chapter: 'basics',
    path: '/',
    target: '.tabs',
    title: 'Archive is tidying, not deleting',
    body: 'Archived series move to their own tab. They keep working and any link you already shared stays live, so nothing breaks because someone cleaned up.',
  },

  // ── inside a series ───────────────────────────────────────────────────────────────────────
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.page-head',
    title: 'Inside a series',
    body: 'The name is editable here too. Underneath, the count and whether it is ready to record — that warning is the same check that decides if anything would stall on camera.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.add-row',
    title: 'Adding a headline',
    body: 'Two buttons, and the choice matters. REAL means a genuine news story and REVEAL opens the real article. FAKE means one you made up, and REVEAL shows an in-house page instead.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.item-headline',
    title: 'The headline itself',
    body: 'This exact text goes on screen. Long ones shrink automatically so they never crowd the logo, so write it as it should read rather than trimming it to fit.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.item-real-fields',
    title: 'Paste the link and the headline fills itself in',
    body: 'On a REAL headline, paste the article link and the headline is pulled from the page. Edit it afterwards if you like — and if you want the original back, paste the same link again.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.verdict-switch',
    title: 'Switching REAL and FAKE',
    body: 'Flip this at any time. A REAL headline needs a working link; a FAKE one needs either an image or some text on its page. Anything missing is listed at the top of the series in red.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.composer-fields',
    title: 'Building a fake page',
    body: 'Upload images and they sit under the headline, centred, at full size. Add supporting text underneath, and a small footer line. No designer needed — you are composing the page here.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.composer-preview',
    title: 'Exactly what plays on camera',
    body: 'Not an approximation. This preview is the same component the recording uses, so what you see here is what he will see.',
  },
  {
    chapter: 'building',
    id: 'artwork',
    path: (id) => `/rounds/${id}`,
    target: '.field.art-state',
    optional: true,
    title: 'Waiting on artwork',
    body: 'A new fake page arrives already carrying the grey AWAITING IMAGE stand-in and ticked as waiting on artwork, so nobody has to remember to flag it. It is counted on the Series list, held out of "ready to record", and Adorian never sees the stand-in — it is builder-only. Write the brief underneath for whoever draws it; the headline alone is rarely enough.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.field.art-state',
    optional: true,
    title: 'And it clears itself',
    body: 'Upload the finished artwork and the stand-in is removed, the tick comes off, and the page counts as ready. There is no second step to remember. Uploading another stand-in does not clear it — reserving the slot is not doing the work.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.item-actions',
    title: 'Reorder, open, delete',
    body: 'The arrows nudge a headline up or down, and you can drag the handle on the left to move it further. Open Live shows that one page on its own, exactly as it will play.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.round-settings',
    title: 'How every page in the series looks',
    body: 'Set once here, applied to all of them. Headline size nudges the text; the top left banner is the FABRICATED flash on fake pages; the top right logo is the Adorian wordmark. Turn either off to give a long headline more room.',
  },
  {
    chapter: 'building',
    path: (id) => `/rounds/${id}`,
    target: '.head-actions',
    title: 'Review, share, import, history',
    body: 'Review Series plays the whole thing as Adorian would. Share Link makes a public link that needs no sign-in. Import / Export moves headlines in and out as a spreadsheet. History shows every change and who made it.',
  },

  // ── before a shoot ────────────────────────────────────────────────────────────────────────
  {
    chapter: 'shoot-day',
    id: 'shoot-ready',
    path: (id) => `/rounds/${id}`,
    target: '[data-testid="shoot-ready"]',
    title: 'Check every link before you record',
    body: 'Opens all of them and confirms each still lands on its story — not a 404, not a homepage, not a bot wall, not a different article. Links rot on their own, so a series that was clean last month is not necessarily clean today. A dead reveal is the worst failure this game has.',
  },
  {
    chapter: 'shoot-day',
    path: (id) => `/rounds/${id}`,
    target: '[data-testid="shoot-ready"]',
    title: 'The results group by reason',
    body: 'A 404, a redirect to a homepage and a page that now shows a different story are three different jobs, so they are listed apart. Each headline underneath is a link — click it and you land on that one. An outlet that refuses the check is reported separately: this runs from a datacenter, and some sites block that even when the article opens fine for you.',
  },
  {
    chapter: 'shoot-day',
    id: 'offline',
    path: (id) => `/rounds/${id}`,
    target: '.offline-row',
    title: 'Take a copy that needs no internet',
    body: 'Downloads the whole series as one file — the app, the fonts and every fake page\'s artwork inside it. Double-click it on any machine and it plays with the network off. Worth having on a shoot: a real headline\'s reveal still opens the live article, but the fake pages never need us at all.',
  },
  {
    chapter: 'shoot-day',
    id: 'trash',
    path: (id) => `/rounds/${id}`,
    target: '.panel',
    setUp: 'open-trash',
    title: 'Deleting is not permanent',
    body: 'Everything removed from a list waits here for 30 days, artwork and all, and Restore puts it back exactly as it was. That covers the worst button in the app: a CSV Replace Everything wipes the round before importing, and a CSV cannot carry images — so the artwork is only recoverable because of this.',
  },
  {
    chapter: 'shoot-day',
    path: (id) => `/rounds/${id}`,
    target: '.panel',
    setUp: 'open-plays',
    title: 'What was actually played',
    body: 'Every time this series was opened and started: how far it got, the real-versus-fake tally, and where from. A team preview is listed too and never counts as a recording — which is why finishing a real play is what marks the series Recorded for you.',
  },

  // ── the two banks ─────────────────────────────────────────────────────────────────────────
  /* One Endless Queue until 2026-08-21, and the artwork backlog was invisible inside it: the
     real headlines needed nothing, every fake one was waiting on a drawing, and a single count
     sat over both. So the tour teaches two banks with two different jobs — which is what they
     are — and then teaches that Adorian still gets one merged run, which is what he gets. */
  {
    chapter: 'endless',
    path: '/endless',
    setUp: 'close-panels',
    target: '.page-head',
    title: 'Real Headlines',
    body: 'Every genuine headline that is not in a series lives here, with no episode around it. These are found rather than written. The browser extension files straight in here; the collectors never do — what they gather waits in Intake until someone moves it across. A real headline is finished the moment it has a link that works. Nothing in here belongs to a series, and nothing in a series turns up in here.',
  },
  {
    chapter: 'endless',
    id: 'banks',
    path: '/endless',
    target: '.app-nav',
    title: 'Why the fake ones are kept apart',
    body: 'They were one queue until August. Real and fake are two different jobs, though: a real headline is found and then it is done, while a fake one is written and then has to be drawn. Together in one list the entire artwork backlog sat behind headlines that needed nothing, and no count anywhere said so. Now each bank carries its own number up here, and each adds only its own kind — this one offers + Add Real Headline and nothing else.',
  },
  {
    chapter: 'endless',
    path: '/endless',
    target: '.round-settings',
    title: 'How every page in this bank looks',
    body: 'Set once here and applied to all of them: how big the headline sits, and whether the Adorian wordmark shows in the corner. The FABRICATED banner is set on the Fake Headlines bank instead, since it only ever draws on a fake page. There is no running order to choose — Endless Mode shuffles the two banks together, so arranging an order is a series job.',
  },
  {
    chapter: 'endless',
    id: 'readiness',
    path: '/endless',
    target: '.trouble',
    optional: true,
    title: 'What is not ready, in one line',
    body: 'Anything that would stall a recording is collected into a red banner at the top of the list, grouped by what is wrong rather than one line per headline — twelve pages waiting on artwork is one row, not twelve sentences. The count is problem headlines out of all of them. Hide collapses it and leaves the count, so it can be got out of the way without being forgotten. It is on every list in the app, and it is absent right now because nothing on this bank is unready — which is the state you are aiming for. On this bank the problem is usually a missing link; on the fake one it is usually the artwork.',
  },
  {
    chapter: 'endless',
    id: 'readiness-jumps',
    path: '/endless',
    target: '.trouble-jumps',
    optional: true,
    title: 'Every number is a link',
    body: 'Each row of that banner ends in a list of numbers, and they are the positions of the headlines with that problem. Click one and it scrolls straight to that headline and marks it for a moment, so you never scroll a list of fifty hunting for number 39. It works even when a filter is hiding that headline — the filter clears itself so the link can land.',
  },
  {
    chapter: 'endless',
    id: 'show-only',
    path: '/endless',
    target: '.trouble-only',
    optional: true,
    title: 'Or work through them as a batch',
    body: 'Every row of that banner also carries a Show only these button, which narrows the list to exactly the headlines in that row and nothing else — the whole backlog on screen with everything finished out of the way. Clear on the filter bar puts the full list back. Nothing is deleted or moved; it is only what you are looking at.',
  },
  {
    chapter: 'endless',
    id: 'filters',
    path: '/endless',
    target: '.filter-bar',
    setUp: 'clear-filters',
    title: 'Finding one headline',
    body: 'Search runs over the headline, the outlet and the link, and every word has to match rather than the whole phrase. Beside it: Real or Fake — which earns its keep in a series, since a bank holds only one of them — and the three states worth chasing: waiting on artwork, missing a link, no published date. Reordering switches off while a filter is on, because the arrows move a headline past ones you cannot see.',
  },
  {
    chapter: 'endless',
    id: 'decades',
    path: '/endless',
    target: '.decade-pick',
    title: 'The decade counts are buttons too',
    body: 'The brief asks for headlines spanning a few decades, and this is how you check. Click one to see only that decade; click it again to clear. Undated is the useful one — it is the worklist of headlines whose publication date nobody has filled in. That is the date the article ran, not the Arrived date on the card, which is filled in for everything. The counts describe the whole list and deliberately do not move when you press one.',
  },
  {
    chapter: 'endless',
    path: '/endless',
    target: '.item-pick',
    setUp: 'pick-first',
    title: 'Grab headlines out of the bank',
    body: 'Tick any number of headlines here. This is the answer to "grab me ten for tomorrow".',
  },
  {
    chapter: 'endless',
    path: '/endless',
    target: '.move-bar',
    setUp: 'pick-first',
    title: 'And move them into a series',
    body: 'Pick an existing series or name a new one and they move across — out of this bank entirely, so he cannot get the same headline twice. Both banks are in that list as well, which is the one-click fix for a headline filed in the wrong one. It works in reverse too: select them in a series and send them back here.',
  },
  {
    chapter: 'endless',
    path: '/endless',
    target: '.head-actions',
    setUp: 'unpick-all',
    title: 'Play Endless',
    body: 'Opens the queue exactly as Adorian will see it — both banks merged into one run and shuffled, so the order never gives a verdict away before he has read the words. Worth a look before you hand anything over.',
  },

  // ── the fake bank ─────────────────────────────────────────────────────────────────────────
  {
    chapter: 'fake',
    id: 'fake-bank',
    path: '/fake',
    setUp: 'close-panels',
    target: '.page-head',
    title: 'Fake Headlines',
    body: 'The other half, written in-house rather than found. The line under the title is the number this bank exists to show: how many of these are still waiting on artwork. Mixed in with the real ones that number had nowhere to appear, and the backlog sat invisible behind a pile of headlines that needed nothing at all.',
  },
  {
    chapter: 'fake',
    id: 'artwork-backlog',
    path: '/fake',
    target: '.trouble',
    optional: true,
    title: 'The artwork backlog is this list',
    body: 'The same not-ready panel as everywhere else, except on this bank it is nearly always one job. Every number jumps to that headline, and Show only these puts the whole backlog on screen with the finished pages out of the way — which is the queue to work down before a shoot.',
  },
  {
    chapter: 'fake',
    path: '/fake',
    target: '.round-settings',
    title: 'The FABRICATED banner is set here',
    body: 'The corner banner belongs to this bank and only this bank, because it only ever draws on a fake page. What you set here is what shows when these headlines come round in Endless Mode. Headline size and the wordmark work as they do everywhere else.',
  },
  {
    chapter: 'fake',
    path: '/fake',
    target: '.move-bar',
    setUp: 'pick-first',
    title: 'How a fake headline reaches him',
    body: 'Two ways, and there is no Play button on this screen on purpose — a run of nothing but fabricated headlines is not the game. Either move it into a series, exactly as you would from the other bank, or leave it here and it comes round shuffled in among the real ones in Endless Mode.',
  },
  {
    chapter: 'fake',
    path: '/fake',
    target: '.add-row',
    setUp: 'unpick-all',
    title: 'Writing a new one',
    body: 'Only + Add Fake Headline is offered here — the real one is on the other bank, because a headline filed in the wrong bank is invisible in the only place anyone would look for it. Every new fake page is born flagged as waiting on artwork, so it joins the list at the top of this screen the moment you write it.',
  },
  // ── the intake queue ──────────────────────────────────────────────────────────────────────
  {
    chapter: 'intake',
    path: '/intake',
    target: '.page-head',
    title: 'Headlines that arrive on their own',
    body: 'Collectors run every morning and drop anything new they find in here. Every one is fact-checked against live sources first, so anything that does not stand up is thrown out before you ever see it. What they cannot judge is whether a headline is funny — that is the job on this screen, and it is the only part that cannot be automated.',
  },
  {
    chapter: 'intake',
    path: '/intake',
    target: '.explainer',
    /* The explainer collapses now, and this step describes what is INSIDE it — collapsed it is a
       25px bar reading "How this works", so without this the step points at a strip and narrates
       three things nobody can see. Restored on finish, because the collapsed state is a
       preference the reader chose. */
    setUp: 'open-explainer',
    title: 'Check it, keep it, or bin it',
    body: 'Three things you can do, and they are on the screen in the order you do them. Nothing here reaches Adorian until someone moves it — Intake is hidden from him twice over, by the list he can see and by the status it carries.',
  },
  {
    chapter: 'intake',
    path: '/intake',
    target: '.link-row',
    title: 'Always open the link first',
    body: 'Open takes you to the article in a new tab. The link is what proves the headline on camera, so if it does not open, or it is not the story the headline promises, the headline is no use however good it reads.',
  },
  {
    chapter: 'intake',
    path: '/intake',
    id: 'intake-approve',
    target: '.move-bar',
    setUp: 'pick-first',
    title: 'Approving one — to a series, or to Endless',
    body: 'Tick anything worth having, then pick where it goes: an existing series, a brand new one you name right here, or a bank. The collectors only ever bring real ones, and each has been fact-checked, so Real Headlines is the usual answer. Tick as many as you like and they all go together, which is how you turn a morning of review into one click.',
  },
  {
    chapter: 'intake',
    path: '/intake',
    target: '[data-testid="reject-btn"]',
    setUp: 'pick-first',
    title: 'Removing one, for good',
    body: 'Reject remembers the link, so the collectors will never offer that article again — which is what stops the same rejects arriving every morning. Deleting a candidate does exactly the same thing: on this screen both mean no, so both teach the collectors. Either way it is recoverable from Recently Deleted for 30 days. Be as ruthless as you like; there is always more.',
  },
  {
    chapter: 'intake',
    path: '/intake',
    setUp: 'unpick-all',
    title: 'What good looks like',
    body: 'The bar is the show itself: something absurd enough that he says "no way that\'s true", complete in one line, with the surprise inside the sentence. A bear honking a horn all night, not a bear returned to its enclosure.',
  },

  // ── his side ──────────────────────────────────────────────────────────────────────────────
  {
    chapter: 'handover',
    target: '.nav-action',
    title: 'What Adorian opens',
    body: 'This is his front door, and the only link he needs. It offers him a premade series — the ones you set to Ready to Record — or Endless Mode, which merges both banks into one shuffled run. He never sees the split; it is there so the work of stocking them can be told apart. Opens in a new tab so you never lose your place.',
  },
  {
    chapter: 'handover',
    title: 'That is the whole tool',
    body: 'Build series for planned episodes, stock the two banks for everything else, and set a series to Ready to Record when it is his to pick up. Intake feeds Real Headlines; the fake bank is where the artwork backlog is visible; the red banner tells you what is not finished; nothing you delete is really gone for a month. Take Tour is in the bar whenever you want it again, and the small ? beside a feature opens the tour at that feature.',
  },
];

/* Which steps a run contains.
 *
 * No argument, or one nothing recognises, means the whole tour — an unrecognised deep link
 * opening an EMPTY tour is the worst of the three outcomes, and silently showing the full tour
 * is the mildest.
 *
 * A recognised value runs from that point to the END OF ITS CHAPTER and stops. Clicking the "?"
 * beside the readiness banner and then being walked through Intake and Adorian's front door is
 * not what that "?" offered.
 */
export function stepsFrom(startAt?: string): TourStep[] {
  if (!startAt) return TOUR_STEPS;

  const byId = TOUR_STEPS.findIndex((s) => s.id === startAt);
  const from = byId >= 0
    ? byId
    : TOUR_STEPS.findIndex((s) => s.chapter === startAt);
  if (from < 0) return TOUR_STEPS;

  const { chapter } = TOUR_STEPS[from];
  let to = from;
  while (to + 1 < TOUR_STEPS.length && TOUR_STEPS[to + 1].chapter === chapter) to += 1;
  return TOUR_STEPS.slice(from, to + 1);
}

/** Every id something can link to, so a typo in a link is findable rather than silent. */
export const TOUR_LINK_IDS = TOUR_STEPS.map((s) => s.id).filter(Boolean) as string[];
